import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import { useToast } from "../../../../components/Toast/ToastProvider";
import BackButton from "../../../../components/Button/BackButton";
import VendorView from "./GeneralInfo/VendorView";
import GeneralInfoEditForm from "./GeneralInfo/GeneralInfoEditForm";
import PrimaryUserEditForm from "./GeneralInfo/PrimaryUserEditForm";
import SettingsEditForm from "./GeneralInfo/SettingsEditForm";
import ContactPersonsPage from "./ContactPersons/ContactPersonPage";
import DocumentsPage from "./VendorDocuments/DocumentsPage";
import VendorServicesPage from "./Services/VendorServicesPage";
import VendorLanguagePairsPage from "./LanguagePairs/VendorLanguagePairsPage"
import VendorSpecializationsPage from "./Specializations/VendorSpecializationsPage";
import PaymentMethodsPage from "./PaymentMethods/PaymentMethodPage";
import PriceListPage from "./PriceList/PriceListPage";
import { confirmDialog } from "../../../../components/Toast/toast";

const tabs = [
  "General Info",
  "Contact Persons",
  "Payment Methods",
  "Services",
  "Language Pairs",
  "Specializations",
  "Price List",
  "Taxes",
  "Documents",
  "Availability Charts",
];

const toSlug = (label) => label.toLowerCase().replace(/\s+/g, "-");
const SLUG_TO_TAB = Object.fromEntries(tabs.map((t) => [toSlug(t), t]));

const VendorDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const activeTab = SLUG_TO_TAB[searchParams.get("tab")] ?? "General Info";
  const VALID_EDIT_SECTIONS = ["general", "primary", "settings"];
  const editParam = searchParams.get("edit");
  const editingSection =
    activeTab === "General Info" && VALID_EDIT_SECTIONS.includes(editParam)
      ? editParam
      : null;
  const setActiveTab = (tab) => {
    setSearchParams({ tab: toSlug(tab) }, { replace: true });
  };
  const setEditingSection = (section) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", toSlug(activeTab));
    if (section) {
      next.set("edit", section);
    } else {
      next.delete("edit");
    }
    setSearchParams(next, { replace: true });
  };
  const tabActionParam = searchParams.get("action");
  const isInSubView = !!editingSection || !!tabActionParam;
  const exitSubView = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    next.delete("action");
    next.delete("id");
    setSearchParams(next, { replace: true });
  };
  const [resendingInvitation, setResendingInvitation] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const fetchVendor = async () => {
      try {
        const response = await api.get(`/vendors/${id}`, {
          withCredentials: true,
        });
        setVendor(response.data.data);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch vendor details");
      } finally {
        setLoading(false);
      }
    };
    fetchVendor();
  }, [id]);

  const handleSave = async (updatedData) => {
    try {
      const response = await api.put(`/vendors/${id}`, updatedData, {
        withCredentials: true,
      });
      setVendor(response.data.data);
      setEditingSection(null);
    } catch (err) {
      console.error("Update failed", err);
      toast.error("Failed to update vendor");
    }
  };

  const handleResendInvitation = async () => {
    if (resendingInvitation) return;
    setResendingInvitation(true);
    try {
      const response = await api.post(
        `/vendors/${id}/resend-invitation`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data?.message || "Activation email has been resent.");
    } catch (err) {
      console.error("Resend invitation failed", err);
      toast.error(err.response?.data?.message || "Failed to resend invitation.");
    } finally {
      setResendingInvitation(false);
    }
  };

  const handleDelete = async () => {
    if (!await confirmDialog("Are you sure you want to delete this vendor?")) return;

    try {
      await api.delete(`/vendors/${id}`, { withCredentials: true });
      toast.success("Vendor deleted successfully");
      navigate("/vendors");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete vendor");
    }
  };

  if (loading)
    return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  if (error)
    return (
      <div className="text-center mt-10 text-red-600 font-medium">{error}</div>
    );

  return (
    <>
        <div className="border-b mb-6 flex items-end gap-4">
          <BackButton
            label={null}
            className="mb-1.5 -ml-1.5"
            onClick={isInSubView ? exitSubView : undefined}
          />
          <div className="h-5 w-px bg-gray-200 mb-2 self-center" />
          <div className="flex space-x-5 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab}
                className={`pb-2 text-sm text-gray-600 border-b-2 transition whitespace-nowrap ${
                  activeTab === tab
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent hover:text-gray-900 hover:border-gray-300"
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "General Info" && (
          <>
            {!editingSection ? (
              <VendorView
                vendor={vendor}
                onEditGeneral={() => setEditingSection("general")}
                onEditPrimary={() => setEditingSection("primary")}
                onEditSettings={() => setEditingSection("settings")}
                onDelete={handleDelete}
                onResendInvitation={handleResendInvitation}
                resendingInvitation={resendingInvitation}
              />
            ) : editingSection === "general" ? (
              <GeneralInfoEditForm
                vendor={vendor}
                onSave={handleSave}
                onCancel={() => setEditingSection(null)}
              />
            ) : editingSection === "primary" ? (
              <PrimaryUserEditForm
                vendor={vendor}
                onSave={handleSave}
                onCancel={() => setEditingSection(null)}
              />
            ) : editingSection === "settings" ? (
              <SettingsEditForm
                vendor={vendor}
                onSave={(updated) => {
                  console.log("Save settings info", updated);
                  setEditingSection(null);
                }}
                onCancel={() => setEditingSection(null)}
              />
            ) : null}
          </>
        )}

        {activeTab === "Contact Persons" && (
          <ContactPersonsPage vendorId={id} vendor={vendor} />
        )}

        {activeTab === "Payment Methods" && (
          <PaymentMethodsPage vendorId={id} />
        )}

        {activeTab === "Services" && (
          <VendorServicesPage vendorId={id} />
        )}

        {activeTab === "Language Pairs" && (
          <VendorLanguagePairsPage vendorId={id} />
        )}

        {activeTab === "Specializations" && (
          <VendorSpecializationsPage vendorId={id} />
        )}

        {activeTab === "Price List" && (
          <PriceListPage vendorId={id} />
        )}

        {/* 

        {activeTab === "Taxes" && (
          <TaxesPage vendorId={id} />
        )} */}

        {activeTab === "Documents" && (
          <DocumentsPage vendorId={id} />
        )}

        {/* {activeTab === "Availability Charts" && (
          <AvailabilityChartsPage vendorId={id} />
        )} */}

    </>
  );
};

export default VendorDetailPage;